import * as React from 'react';
import styled from 'styled-components';
import {
  Button,
  makeStyles,
  DialogActions,
  DialogContent,
  DialogTitle,
  Theme,
  Typography
} from '@material-ui/core';
import ProgressBar from '../progress/ProgressBar';
import CircularIndicator from '../progress/CircularIndicator';
import { CSVLink } from 'react-csv';
import { Styles } from '@material-ui/styles/withStyles';

export interface FreeContentDialogButton {
  text: string;
  onClick: (e: any) => any;
  disabled?: boolean;
}

export enum FreeContentDialogProcessingType {
  // 線がなんとなく伸びるやつ（propsを省略するとこれになる）
  linearIndeterminate = 'linearIndeterminate',
  // ボタンがくるくるするやつ
  button = 'button',
  // 進捗に合わせて線が伸びるやつ（processingValue必須）。これだけだと進捗がない間がつらいためボタンもくるくるする。
  linearDeterminate = 'linearDeterminate'
}

/**
 * makeStylesをラップして、useStylesの戻りに型がつくようにしたもの
 * @param style
 */
const createUseStyles = <T extends Styles<Theme, any>>(
  style: T | ((theme: Theme) => T)
) => makeStyles(style as any) as unknown as (props?: any) => { [K in keyof T]: string };

const useStyles = createUseStyles(theme => ({
  link: {
    color: '#029FAC',
    textDecoration: 'none'
  },
  disabledLink: {
    color: '#757575',
    textDecoration: 'none'
  }
}));

interface P {
  children: any;
  title: string;
  processing: boolean;
  processingType?: FreeContentDialogProcessingType;
  processingValue?: number;
  confirmButton?: FreeContentDialogButton;
  cancelButton?: FreeContentDialogButton;
  destructiveButton?: FreeContentDialogButton;
  titleColor?: boolean;
  dividers?: boolean;
  // フッターで文字が隠れてしまうことを防ぎたいときはtrueにします。
  // スクロールするダイアログでtrueにすると、スクロールができなくなるので、
  // 注意してください。
  isPopupOnDialog?: boolean;
  csvHeaders: {
    label: string;
    key: string;
  }[];
  onClickDownload: () => any;
  fileName: string;
  csvRows: string | object[];
}

const DeterminateProgressWrapper = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * これを使ったコンポーネントを作り、呼び出し側では<FreeContentDialog>で囲む
 * 参考: SpacesのSpaceGroupRegistrationDialog
 * @param props
 * @constructor
 */
const FreeCsvDownloadDialog: React.FC<P> = (props: P) => {
  const {
    children,
    processing,
    processingType,
    processingValue,
    title,
    confirmButton,
    cancelButton,
    destructiveButton,
    titleColor,
    dividers,
    isPopupOnDialog,
    csvHeaders,
    fileName,
    csvRows
  } = props;
  const classes = useStyles();

  return (
    <div style={{ minWidth: 540 }}>
      {processing &&
        (!processingType ||
          processingType ===
            FreeContentDialogProcessingType.linearIndeterminate) && (
          <ProgressBar />
        )}
      <DialogTitle
        style={
          titleColor
            ? {
                backgroundColor: 'var(--color-admin-key)'
              }
            : {}
        }
      >
        <Typography
          style={
            titleColor
              ? {
                  color: '#ffffff'
                }
              : {}
          }
        >
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent
        dividers={dividers}
        style={isPopupOnDialog ? { overflowY: 'initial' } : {}}
      >
        {children}
      </DialogContent>
      <DeterminateProgressWrapper>
        {(processing || !!processingValue) &&
          processingType ===
            FreeContentDialogProcessingType.linearDeterminate && (
            <ProgressBar variant={'determinate'} value={processingValue} />
          )}
      </DeterminateProgressWrapper>
      <DialogActions>
        {cancelButton && (
          <Button
            id={'freeContentDialogCancelButton'}
            disabled={processing || cancelButton.disabled}
            onClick={cancelButton.onClick}
          >
            {cancelButton.text}
          </Button>
        )}
        {destructiveButton && (
          <Button
            id={'freeContentDialogDestructiveButton'}
            disabled={processing || destructiveButton.disabled}
            onClick={destructiveButton.onClick}
          >
            <Typography color={'error'}>{destructiveButton.text}</Typography>
            {processing &&
              (processingType === FreeContentDialogProcessingType.button ||
                processingType ===
                  FreeContentDialogProcessingType.linearDeterminate) && (
                <CircularIndicator />
              )}
          </Button>
        )}
        {confirmButton && (
          <Button
            id={'freeContentDialogConfirmButton'}
            disabled={processing || confirmButton.disabled}
            color={'primary'}
          >
            <CSVLink
              headers={csvHeaders}
              filename={fileName}
              onClick={confirmButton.onClick}
              data={csvRows}
              target={'_blank'}
              className={
                confirmButton.disabled ? classes.disabledLink : classes.link
              }
            >
              {confirmButton.text}
            </CSVLink>
            {processing &&
              (processingType === FreeContentDialogProcessingType.button ||
                processingType ===
                  FreeContentDialogProcessingType.linearDeterminate) && (
                <CircularIndicator />
              )}
          </Button>
        )}
      </DialogActions>
    </div>
  );
};

export default FreeCsvDownloadDialog;


